import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

function Metric({ label, value, testId, tone = "" }) {
  return (
    <div className={`border p-4 ${tone === "attention" ? "border-red-400/40 bg-red-400/5" : "border-cream/10"}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">{label}</p>
      <p className={`font-display text-3xl mt-1 ${tone === "attention" ? "text-red-300" : "text-cream"}`}>{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <p className="overline mb-3">{title}</p>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

export default function HQHome() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/hq/overview").then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);

  if (err) return <div className="p-8 text-red-300" data-testid="hq-home-error">{err}</div>;
  if (!d) return <div className="p-8 text-cream/50" data-testid="hq-home-loading">Loading…</div>;

  const attention = (d.needs_attention.open_service_disputes + d.needs_attention.open_safety_incidents + d.needs_attention.verification_queue) > 0;

  return (
    <div className="p-8 max-w-6xl" data-testid="hq-home">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <p className="overline">Control Room</p>
          <h1 className="font-display text-4xl tracking-tighter">Does anything need our attention right now?</h1>
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">Generated {new Date(d.generated_at).toLocaleString()}</p>
      </div>

      {attention && (
        <div className="mb-6 border border-red-400/40 bg-red-400/5 p-3 text-xs text-red-300" data-testid="hq-attention-banner">
          {d.needs_attention.open_safety_incidents > 0 && <>⚠ {d.needs_attention.open_safety_incidents} open safety incident(s). </>}
          {d.needs_attention.open_service_disputes > 0 && <>⚑ {d.needs_attention.open_service_disputes} open service dispute(s). </>}
          {d.needs_attention.verification_queue > 0 && <>ℹ {d.needs_attention.verification_queue} verification case(s) waiting. </>}
        </div>
      )}

      <Section title="Needs attention">
        <Link to="/hq/safety"><Metric testId="hq-metric-open-safety"    label="Open safety incidents"  value={d.needs_attention.open_safety_incidents} tone={d.needs_attention.open_safety_incidents ? "attention" : ""} /></Link>
        <Link to="/hq/disputes"><Metric testId="hq-metric-open-disputes" label="Open service disputes"  value={d.needs_attention.open_service_disputes} tone={d.needs_attention.open_service_disputes ? "attention" : ""} /></Link>
        <Link to="/hq/verification"><Metric testId="hq-metric-verif-queue" label="Verification queue"    value={d.needs_attention.verification_queue} tone={d.needs_attention.verification_queue ? "attention" : ""} /></Link>
        <Link to="/hq/after-hours"><Metric testId="hq-metric-ah-pending"  label="After-hours pending"    value={d.live_now.after_hours_pending} /></Link>
      </Section>

      <Section title="Live now">
        <Metric testId="hq-metric-active"       label="Active appointments" value={d.live_now.active_appointments} />
        <Metric testId="hq-metric-today"        label="Bookings today"      value={d.live_now.bookings_today} />
        <Metric testId="hq-metric-completed"    label="Completed today"     value={d.live_now.completed_today} />
        <Metric testId="hq-metric-ah-live"      label="After-hours pending" value={d.live_now.after_hours_pending} />
      </Section>

      <Section title="Finance (all time — DEMO)">
        <Metric testId="hq-metric-gross"    label="Gross service value"        value={`$${d.finance_snapshot_all_time.gross_service_value}`} />
        <Metric testId="hq-metric-pro-earn" label="Professional earnings"      value={`$${d.finance_snapshot_all_time.professional_earnings}`} />
        <Metric testId="hq-metric-fees"     label="KUTZ platform fees"         value={`$${d.finance_snapshot_all_time.kutz_platform_fees}`} />
        <Metric testId="hq-metric-waivers"  label="Fee waivers (repeat)"       value={`${d.finance_snapshot_all_time.fee_waivers_count} · $${d.finance_snapshot_all_time.fee_waivers_value_demo}`} />
      </Section>

      <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40">All values shown are prototype/demo. No real funds have moved.</p>
    </div>
  );
}
